// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import DataTable from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionTitle from "@werner/App/Components/Common/SectionTitle";
import { ProductCardView } from "@werner/App/Components/Product/common";
import XngCarousel from "@werner/App/Components/XngCarousel";
import PurchasedProductCard, { PurchasedProductCardStyles } from "@werner/Overrides/Components/PurchasedProductCard";
import { SLIDES_PER_VIEW_POINTS } from "@werner/XDL/constants";
import { generateSlidesPerViewMap, xScreen } from "@werner/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    // XNG-Changes
    productView = "productView",
    categoryId = "categoryId",
    linkTitle = "linkTitle",
    linkUrl = "linkUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        // XNG-Changes
        [fields.productView]: ProductCardView;
        [fields.categoryId]: string[];
        [fields.linkTitle]: string;
        [fields.linkUrl]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,
        // XNG-Changes
        productSettings: getSettingsCollection(state).productSettings,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;
    // XNG-Changes:
    productsContainer?: InjectableCss;
    purchasedProductCard?: PurchasedProductCardStyles;
    dataTableBody?: DataTableBodyProps;
}

// XNG-Changes: custom PurchasedProductCard style
export const purchasedProductsStyles: PurchasedProductsStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    headerContainer: {
        gap: 0,
        css: css`
            padding: 10px;
            background-color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    itemHeaderGridItem: { width: [0, 0, 6, 6, 6] },
    itemHeaderText: { weight: "bold" },
    priceHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-right: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceHeaderText: { weight: "bold" },
    addToCartHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    addToCartHeaderText: { weight: "bold" },
    productsContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            gap: 33px;
        `,
    },
};

const styles = purchasedProductsStyles;

const getJustifyContent = (position: string) => {
    if (position === "left") {
        return "flex-start";
    }
    if (position === "right") {
        return "flex-end";
    }
    return "center";
};

const TitleWrapper = styled.div<{ position: string }>`
    display: flex;
    justify-content: ${({ position }) => getJustifyContent(position)};
`;

const PurchasedProducts = ({
    id,
    fields,
    products,
    canViewPurchasedProducts,
    loadPurchasedProducts,
    productSettings,
}: Props) => {
    const { productView, title, linkTitle, linkUrl } = fields;

    const sectionTitleProps = {
        title,
        viewMoreLink: linkUrl,
        viewMoreText: linkTitle,
    };

    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({
                widgetId: id,
                purchaseType: fields.purchaseType,
                // XNG-Changes: add categoryId to the request
                ...{ categoryId: productView === "table" ? "" : fields.categoryId?.[0] },
            });
        }
    }, [fields.purchaseType, fields.categoryId?.[0], productView]);

    if (!canViewPurchasedProducts || !products || products.length === 0) {
        return null;
    }

    // XNG-Changes: change to table instead of grid container
    if (productView === "table") {
        return (
            <>
                <SectionTitle {...sectionTitleProps} />
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader tight alignX="left" colSpan={2}>
                            {translate("Product Information")}
                        </DataTableHeader>
                        {fields.showPrice && (
                            <DataTableHeader tight alignX="center">
                                {translate("Price")}
                            </DataTableHeader>
                        )}
                        {fields.showAvailability && (
                            <DataTableHeader tight alignX="center">
                                {translate("Availability")}
                            </DataTableHeader>
                        )}
                        {fields.showAddToCart && <DataTableHeader tight alignX="center"></DataTableHeader>}
                    </DataTableHead>
                    <DataTableBody {...styles.dataTableBody}>
                        {products.map(product => (
                            // XNG-Changes: send custom style as a props
                            <PurchasedProductCard
                                extendedStyles={styles.purchasedProductCard}
                                key={product.id}
                                product={product}
                                widgetId={id}
                                showBrand={fields.showBrand}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAvailability={productSettings.showInventoryAvailability && fields.showAvailability}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList}
                                {...{ cardView: productView }}
                            />
                        ))}
                    </DataTableBody>
                </DataTable>
            </>
        );
    }
    if (productView === "grid") {
        const breakpoints = generateSlidesPerViewMap({
            [SLIDES_PER_VIEW_POINTS.xs]: 2,
            [SLIDES_PER_VIEW_POINTS.sm]: 3,
            [SLIDES_PER_VIEW_POINTS.md]: 4,
            [SLIDES_PER_VIEW_POINTS.lg]: 5,
        });
        return (
            <>
                <SectionTitle {...sectionTitleProps} />
                <XngCarousel spaceBetween={33} breakpoints={breakpoints}>
                    {products.map(product => (
                        <PurchasedProductCard
                            key={product.id}
                            product={product}
                            widgetId={id}
                            showBrand={fields.showBrand}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAvailability={fields.showAvailability}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            {...{ cardView: productView }}
                        />
                    ))}
                </XngCarousel>
            </>
        );
    }

    return (
        <>
            <SectionTitle {...sectionTitleProps} />
            <StyledWrapper {...styles.productsContainer}>
                {(products || []).slice(0, 6).map(product => {
                    return (
                        <PurchasedProductCard
                            key={product.id}
                            product={product}
                            widgetId={id}
                            showBrand={fields.showBrand}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAvailability={fields.showAvailability}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            {...{ cardView: productView }}
                        />
                    );
                })}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            // {
            //     name: fields.titlePosition,
            //     displayName: "Title Position",
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { value: "center", displayName: "Center" },
            //         { value: "left", displayName: "Left" },
            //         { value: "right", displayName: "Right" },
            //     ],
            //     defaultValue: "center",
            //     fieldType: "General",
            //     sortOrder: 1,
            // },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            // {
            //     name: fields.showBrand,
            //     displayName: "Brand Name",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: true,
            //     fieldType: "General",
            //     sortOrder: 3,
            // },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
                isVisible: item => ["grid", "mini"].includes(item.fields[fields.productView]),
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
                isVisible: item => ["grid", "table"].includes(item.fields[fields.productView]),
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
                isVisible: item => ["grid", "mini", "table"].includes(item.fields[fields.productView]),
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
                isVisible: item => ["grid", "mini"].includes(item.fields[fields.productView]),
            },
            // XNG-Changes
            {
                name: fields.productView,
                displayName: "Product View",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Table View",
                        value: "table",
                    },
                    {
                        displayName: "Carousel",
                        value: "grid",
                    },
                    {
                        displayName: "2 x 3",
                        value: "mini",
                    },
                    {
                        displayName: "1 x 6",
                        value: "small",
                    },
                ],
                defaultValue: "table",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.categoryId,
                displayName: "Category",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 2,
                isVisible: item => item.fields[fields.productView] !== "table",
                singleCategorySelection: () => false,
            },
            {
                name: fields.linkTitle,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "View More",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.linkUrl,
                displayName: "Link Url",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "Translatable",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
